import { Component, HostListener, OnDestroy, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { TableColumnTypeEnum, TableFilterTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { KakaotalkTemplatesService } from './kakaotalk-templates.service';
import { KakaotalkTemplate } from './shared/models';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { KakaotalkService } from 'src/app/shared/strategies/channels';

@Component({
  selector: 'app-kakaotalk-templates',
  templateUrl: './kakaotalk-templates.component.html',
  styleUrls: ['./kakaotalk-templates.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class KakaotalkTemplatesComponent implements OnDestroy {
  selectedTabIndex = 0;
  config: TableConfig = {
    displayedColumns: ['name', 'category', 'content', 'language', 'last_modified_at', 'site_id', 'actions'],
    columns: [
      {
        name: 'name',
        translationKey: 'parameters/kakaotalk-templates/name',
        property: 'templateNameRef',
        type: TableColumnTypeEnum.TEMPLATE_REF,
        sortEnabled: true,
      },
      {
        name: 'category',
        translationKey: 'parameters/kakaotalk-templates/category',
        property: 'categoryLabel',
      },
      {
        name: 'content',
        translationKey: 'parameters/kakaotalk-templates/content',
        property: 'body',
      },
      {
        name: 'language',
        translationKey: 'parameters/kakaotalk-templates/language',
        property: 'languageLabel',
        sortEnabled: true,
      },
      {
        name: 'last_modified_at',
        translationKey: 'parameters/kakaotalk-templates/last-modification',
        property: 'last_modified_at',
        type: TableColumnTypeEnum.DATE,
        sortEnabled: true,
      },
      {
        name: 'site_id',
        translationKey: 'parameters/kakaotalk-templates/account-id',
        property: 'site',
        type: TableColumnTypeEnum.SITE,
        sortEnabled: true,
      },
    ],
    actions: [
      {
        tooltip: 'delete',
        clickFn: element => this.askForDelete(element),
        icon: 'delete',
        color: 'warn',
      },
    ],
  };
  filters: TableFilters = {
    last_modified_at: {
      name: 'lastEditionDate',
      placeholder: '',
      type: TableFilterTypeEnum.DATE,
    },
    site_id: {
      name: 'accountIds',
      placeholder: 'parameters/kakaotalk-templates/account-id',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
    language: {
      name: 'languages',
      placeholder: 'parameters/kakaotalk-templates/language',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
  };
  private subscriptions = new Subscription();

  @ViewChild('templateNameRef', { static: true }) templateNameRef: TemplateRef<any>;

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }

  constructor(
    public dialog: MatDialog,
    private searchBarService: SearchBarService,
    private kakaotalkTemplatesService: KakaotalkTemplatesService,
    private basicService: BasicService,
    private sitesService: SitesService,
    kakaotalkService: KakaotalkService
  ) {
    this.searchBarService.sort = { active: 'last_modified_at', direction: 'desc' };
    this.searchBarService.referer$ = this.kakaotalkTemplatesService.retrieveTemplates;
    this.searchBarService.formatter = (element: KakaotalkTemplate) => {
      const { site_id, language, category, name, language_group_id } = element;
      element.templateNameRef = {
        templateRef: this.templateNameRef,
        context: {
          url: kakaotalkTemplatesService.getTemplateDetailsPath(language_group_id, site_id),
          name: name,
        },
      };
      element.categoryLabel =
        category && basicService.translate(`parameters/kakaotalk-templates/category/${category}/label`);
      element.languageLabel = this.basicService.translate(language);
      element.site = this.sitesService.enrichSiteWithLogo(this.sitesService.sitesV2Map.get(site_id));
    };

    this.filters.site_id.items = this.sitesService.sitesV2
      .filter(({ type }) => type === kakaotalkService.getType())
      .map(({ id, siteName }) => ({ id, label: siteName }));

    this.filters.language.items = this.kakaotalkTemplatesService.getSupportedLanguages();

    this.searchBarService.submitQuery();
  }

  primaryButtonAction = () => {
    this.kakaotalkTemplatesService.navigateToCreate();
  };

  private askForDelete(template: KakaotalkTemplate) {
    const { id, name } = template;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.basicService.translate('parameters/kakaotalk-templates/remove/title'),
        description: this.basicService.translate('parameters/kakaotalk-templates/remove/confirm', {
          templateName: name,
        }),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('delete'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const { site_id } = template;
        this.basicService.showSpinner();
        this.kakaotalkTemplatesService
          .remove(site_id, id)
          .pipe(finalize(() => this.basicService.hideSpinner()))
          .subscribe({
            next: () => {
              this.searchBarService.searchAndSortBS.next(true);
              this.basicService.toastSuccess('ok');
            },
          });
      }
    });
  }
  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
  }
}
